import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import worksheetService from '../../services/worksheetService';
import WorksheetTemplateBuilderModal from './WorksheetTemplateBuilderModal';
import WorksheetPrintModal from '../worksheet/WorksheetPrintModal';
import '../worksheet/WorksheetWorkbench.css';

const WorksheetTemplateMasterPage = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // Builder Modal State
  const [isBuilderOpen, setIsBuilderOpen] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState(null);

  // Test Print Modal State
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [testPrintTestIds, setTestPrintTestIds] = useState([]);

  const fetchTemplates = async () => {
    setIsLoading(true);
    try {
      const res = await worksheetService.getTemplates({ searchTerm });
      if (res.success && res.data) {
        setTemplates(res.data);
      }
    } catch (err) {
      console.error('Failed to load templates:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, [searchTerm]);

  const handleCreateNew = () => {
    setEditingTemplateId(null);
    setIsBuilderOpen(true);
  };

  const handleEdit = (templateId) => {
    setEditingTemplateId(templateId);
    setIsBuilderOpen(true);
  };

  const handleTestPrint = async (templateId) => {
    try {
      // Get a specimen from the worklist to use for preview/print
      const worklistRes = await worksheetService.getWorklist({ pageSize: 5 });
      const ids = worklistRes.data?.items?.map((i) => i.testId) || [];
      setTestPrintTestIds(ids);
      setIsPrintModalOpen(true);
    } catch (err) {
      console.error('Failed to fetch specimens for test print:', err);
      setIsPrintModalOpen(true);
    }
  };

  const handleClone = async (template) => {
    try {
      const detailRes = await worksheetService.getTemplateById(template.templateId);
      if (detailRes.success && detailRes.data) {
        const copy = detailRes.data;
        const payload = {
          templateName: `${copy.templateName} (Copy)`,
          departmentId: copy.departmentId,
          subCategoryId: copy.subCategoryId,
          layoutType: copy.layoutType,
          groupBy: copy.groupBy,
          sortBy: copy.sortBy,
          sortOrder: copy.sortOrder,
          paperSize: copy.paperSize,
          orientation: copy.orientation,
          margins: copy.margins,
          includeHeader: copy.includeHeader,
          includeFooter: copy.includeFooter,
          includeBarcode: copy.includeBarcode,
          includeSignature: copy.includeSignature,
          isDefault: false,
          isActive: true,
          fields: copy.fields
        };
        await worksheetService.saveTemplate(payload);
        fetchTemplates();
        alert('Template cloned successfully!');
      }
    } catch (err) {
      alert('Failed to clone template: ' + err.message);
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to deactivate this template?')) return;
    try {
      await worksheetService.deleteTemplate(templateId);
      fetchTemplates();
    } catch (err) {
      alert('Failed to deactivate template: ' + err.message);
    }
  };

  return (
    <div className="ws-container" style={{ padding: '20px 28px' }}>
      {/* 1. Direct Banner to Technician Workbench */}
      <div
        style={{
          background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
          color: '#ffffff',
          padding: '16px 22px',
          borderRadius: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px',
          boxShadow: '0 4px 12px rgba(2, 132, 199, 0.25)'
        }}
      >
        <div>
          <div style={{ fontSize: '16px', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>🔬 Looking to Print Laboratory Worksheets / PDFs?</span>
            <span
              style={{
                background: '#ffffff',
                color: '#0284c7',
                padding: '2px 8px',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: 700
              }}
            >
              TECHNICIAN WORKBENCH
            </span>
          </div>
          <div style={{ fontSize: '13px', opacity: 0.95, marginTop: '4px' }}>
            This page is the <strong>Template Master</strong> for designing formats. To view real patient specimens, start testing, and <strong>print/export PDFs</strong>, open the <strong>Technician Workbench</strong>.
          </div>
        </div>

        <button
          type="button"
          className="ws-btn"
          style={{
            background: '#ffffff',
            color: '#0284c7',
            fontWeight: 800,
            padding: '10px 18px',
            borderRadius: '6px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
          onClick={() => navigate('/pathology/worksheet')}
        >
          🚀 Open Technician Workbench ➔
        </button>
      </div>

      {/* 2. Top Header */}
      <div
        style={{
          background: '#ffffff',
          padding: '16px 24px',
          borderRadius: '8px',
          border: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#0f172a' }}>
            📋 Worksheet Template Master
          </h2>
          <p style={{ margin: '3px 0 0 0', fontSize: '13px', color: '#64748b' }}>
            Design, configure, and maintain printable laboratory worksheet formats and column layouts
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <input
            type="text"
            className="ws-filter-input"
            style={{ width: '240px' }}
            placeholder="Search templates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button
            type="button"
            className="ws-btn ws-btn-primary"
            onClick={handleCreateNew}
          >
            + Create New Template
          </button>
        </div>
      </div>

      {/* 3. Templates Table Card */}
      <div className="ws-table-card">
        <div className="ws-table-wrapper">
          <table className="ws-table">
            <thead>
              <tr>
                <th style={{ width: '40px' }}>#</th>
                <th>Template Name</th>
                <th>Layout Architecture</th>
                <th>Section</th>
                <th>Grouping &amp; Sort</th>
                <th>Paper / Orientation</th>
                <th>Status</th>
                <th style={{ width: '280px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                    Loading worksheet templates...
                  </td>
                </tr>
              ) : templates.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                    No templates found matching search criteria.
                  </td>
                </tr>
              ) : (
                templates.map((tpl, idx) => (
                  <tr key={tpl.templateId}>
                    <td>{idx + 1}</td>
                    <td>
                      <div style={{ fontWeight: 700, color: '#0f172a' }}>{tpl.templateName}</div>
                      {tpl.isDefault && (
                        <span className="ws-badge" style={{ background: '#fef3c7', color: '#b45309', fontWeight: 700, marginTop: '2px' }}>
                          ⭐ Default Template
                        </span>
                      )}
                    </td>
                    <td>
                      <span className="ws-badge" style={{ background: '#e0f2fe', color: '#0369a1', fontWeight: 700 }}>
                        {tpl.layoutType}
                      </span>
                    </td>
                    <td>{tpl.sectionName || 'All Pathology'}</td>
                    <td>
                      <div style={{ fontSize: '12px', color: '#334155' }}>
                        Group: <strong>{tpl.groupBy || 'Patient'}</strong>
                      </div>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>
                        Sort: {tpl.sortBy || 'Priority'} ({tpl.sortOrder || 'ASC'})
                      </div>
                    </td>
                    <td>
                      <span className="ws-badge badge-routine">
                        {tpl.paperSize || 'A4'} • {tpl.orientation || 'Portrait'}
                      </span>
                    </td>
                    <td>
                      {tpl.isActive ? (
                        <span className="ws-badge badge-completed">Active</span>
                      ) : (
                        <span className="ws-badge badge-pending">Inactive</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', gap: '6px' }}>
                        <button
                          type="button"
                          className="ws-btn ws-btn-primary ws-btn-sm"
                          onClick={() => handleTestPrint(tpl.templateId)}
                          title="Preview and test print this template"
                        >
                          🖨️ Test Print / PDF
                        </button>
                        <button
                          type="button"
                          className="ws-btn ws-btn-outline ws-btn-sm"
                          onClick={() => handleEdit(tpl.templateId)}
                          title="Open visual designer"
                        >
                          ✏️ Edit
                        </button>
                        <button
                          type="button"
                          className="ws-btn ws-btn-outline ws-btn-sm"
                          onClick={() => handleClone(tpl)}
                          title="Clone this template"
                        >
                          📋 Clone
                        </button>
                        <button
                          type="button"
                          className="ws-btn ws-btn-outline ws-btn-sm"
                          style={{ color: '#dc2626' }}
                          onClick={() => handleDelete(tpl.templateId)}
                          title="Deactivate template"
                        >
                          ✕
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 4. Visual Template Builder Modal */}
      <WorksheetTemplateBuilderModal
        isOpen={isBuilderOpen}
        onClose={() => setIsBuilderOpen(false)}
        templateId={editingTemplateId}
        onSaved={fetchTemplates}
      />

      {/* 5. Live Print & PDF Preview Modal */}
      <WorksheetPrintModal
        isOpen={isPrintModalOpen}
        onClose={() => setIsPrintModalOpen(false)}
        initialTestIds={testPrintTestIds}
        availableTemplates={templates}
      />
    </div>
  );
};

export default WorksheetTemplateMasterPage;
